import {useEffect, useMemo, useState} from "react";
import {
    Table, TableHead, TableBody, TableCell, TableContainer, TableRow,
    TextField, IconButton, Paper, Box, Checkbox, FormControl, Select, MenuItem, Autocomplete, FormHelperText, FormLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from "moment";
import * as React from "react";
import Button from '../../components/Button/Button';

const Columns = ({ value, onChange, schema, appValidated, description, required, error,
                     label, visible, allowAddRows, ...props }: {
    value: any; onChange: (rows: any[]) => void; schema: any; [key: string]: any }) => {
    const [rows, setRows] = useState(value || []);

    // Sync rows with external value changes
    useEffect(() => {
        setRows(value || []);
    }, [value]);


    const [initialDefaults, setInitialDefaults] = useState<Record<string, any>>({});
    const [containerWidth, setContainerWidth] = useState(600);
    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    // Extract default values from schema when component mounts
    React.useEffect(() => {
        if (schema?.items?.properties && Object.keys(initialDefaults).length === 0) {
            const defaults: any = {};
            Object.keys(schema.items.properties).forEach(key => {
                const fieldSchema = schema.items.properties[key];
                const fieldType = getFieldType(fieldSchema);
                const fieldDef = fieldSchema.properties[Object.keys(fieldSchema.properties)[0]];

                // Use schema default if available, otherwise use type-based default
                const defaultValue = fieldDef.default !== undefined ? fieldDef.default : '';

                const valueMap: any = {
                    text: { text: defaultValue },
                    number: { number: fieldDef.default !== undefined ? fieldDef.default : '' },
                    email: { email: defaultValue },
                    phone: { phone: defaultValue },
                    date: { date: fieldDef.default || '' },
                    time: { time: fieldDef.default || '' },
                    select: { select: fieldDef.default || '' },
                    autocomplete: { autocomplete: fieldDef.default || '' }
                };

                defaults[key] = valueMap[fieldType] || { text: defaultValue };
            });
            setInitialDefaults(defaults);
        }
    }, [schema, initialDefaults]);


    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const someRequiredFieldsEmpty = useMemo(() => (value || []).some((item: any) => {
        return Object.entries(item as Record<string, any>).some(([key, nestedObject]) => {
            return Object.entries(nestedObject as Record<string, any>).some(([nestedKey, propertyValue]) => {
                const isFieldRequired = schema?.items?.properties?.[key]?.required?.includes(nestedKey);
                return isFieldRequired && propertyValue === '';
            });
        });
    }), [value, schema]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && someRequiredFieldsEmpty,
        [required, appHasBeenValidated, someRequiredFieldsEmpty]);

    const showError = useMemo(() =>
            error !== undefined && error !== "",
        [error]
    );

    const hasError = showError || showRequiredError;
    const errorMessage = showRequiredError ? 'This field is required' : error;

    const columnSchemas = useMemo(() => {
        return schema?.items?.properties || {};
    }, [schema]);

    const CELL_WIDTH = 180;
    const SMALL_CELL_WIDTH = 80; // For actions column

    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const headerLabels = Object.keys(columnSchemas).map(key => {
        const parts = key.split('_#_');
        const labelParts = parts.slice(0, -1);
        const baseLabel = labelParts.map(part =>
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        ).join(' ');

        // Check if this column is required
        const columnSchema = columnSchemas[key];
        const isRequired = columnSchema.required && columnSchema.required.length > 0;

        return isRequired ? `${baseLabel} *` : baseLabel;
    });


    useEffect(() => {
        const calculateWidth = () => {
            const bodyWidth = document.body.clientWidth;
            setContainerWidth(bodyWidth < 600 ? bodyWidth - 40 : 600);
        };

        // Calculate initial width
        calculateWidth();

        // Add resize listener for responsiveness
        window.addEventListener('resize', calculateWidth);

        return () => window.removeEventListener('resize', calculateWidth);
    }, []);

    const getFieldType = (fieldSchema: any) => {
        const props = fieldSchema.properties;
        return Object.keys(props)[0];
    };

    const addRow = () => {
        const newRow: any = {};

        // Always use the schema-based default values
        Object.keys(columnSchemas).forEach(key => {
            if (initialDefaults[key]) {
                newRow[key] = { ...initialDefaults[key] }; // Create a copy
            } else {
                // Fallback in case initialDefaults isn't set yet
                const fieldType: string = getFieldType(columnSchemas[key]);
                const valueMap: any = {
                    text: { text: '' },
                    number: { number: '' },
                    email: { email: '' },
                    phone: { phone: '' },
                    date: { date: '' },
                    time: { time: '' },
                    select: { select: '' },
                    autocomplete: { autocomplete: '' }
                };
                newRow[key] = valueMap[fieldType] || { text: '' };
            }
        });

        const newRows = [...rows, newRow];
        setRows(newRows);
        onChange(newRows);
    };

    const removeRow = (rowIndex: number) => {
        if (rowIndex === 0) return;
        const updatedRows = rows.filter((_: any, index: number) => index !== rowIndex);
        setRows(updatedRows);
        onChange(updatedRows);
    };

    const handleCellChange = (rowIndex: number, fieldKey: string, newValue: any, fieldType: string) => {
        const updatedRows = [...rows];
        updatedRows[rowIndex][fieldKey][fieldType] = newValue;
        setRows(updatedRows);
        onChange(updatedRows);
    };

    const renderCell = (fieldKey: string, fieldData: any, rowIndex: number) => {
        const fieldSchema = columnSchemas[fieldKey];
        const fieldType = getFieldType(fieldSchema);
        const fieldDef = fieldSchema.properties[Object.keys(fieldSchema.properties)[0]];
        const value = fieldData[Object.keys(fieldData)[0]];
        const required = fieldSchema.required && fieldSchema.required[0] === fieldType;

        switch (fieldType) {
            case 'number':
                return (
                    <TextField
                        type="number"
                        required={required}
                        value={value}
                        onChange={(e) => handleCellChange(rowIndex, fieldKey, e.target.value, Object.keys(fieldData)[0])}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                );

            case 'email':
                return (
                    <TextField
                        type="email"
                        required={required}
                        value={value}
                        onChange={(e) => handleCellChange(rowIndex, fieldKey, e.target.value, Object.keys(fieldData)[0])}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                );

            case 'phone':
                return (
                    <TextField
                        type="tel"
                        required={required}
                        value={value}
                        onChange={(e) => handleCellChange(rowIndex, fieldKey, e.target.value, Object.keys(fieldData)[0])}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                );

            case 'date':
                return (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            value={value ? moment(value) : null} // Convert empty string to null
                            onChange={(newValue) => handleCellChange(
                                rowIndex,
                                fieldKey,
                                newValue ? newValue.format('YYYY-MM-DD') : '', // Convert null back to empty string
                                Object.keys(fieldData)[0]
                            )}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    variant: 'outlined',
                                    required: required,
                                    fullWidth: true
                                }
                            }}
                        />
                    </LocalizationProvider>
                );


            case 'time':
                return (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <TimePicker
                            value={value ? moment(value, 'HH:mm') : null} // Convert empty string to null
                            onChange={(newValue) => handleCellChange(
                                rowIndex,
                                fieldKey,
                                newValue ? newValue.format('HH:mm') : '', // Convert null back to empty string
                                Object.keys(fieldData)[0]
                            )}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    variant: 'outlined',
                                    required: required,
                                    fullWidth: true
                                }
                            }}
                        />
                    </LocalizationProvider>
                );
            case 'select':
                const selectOptions = fieldDef.enum || [];
                return (
                    <FormControl fullWidth size="small">
                        <Select
                            required={required}
                            value={value}
                            onChange={(e) => handleCellChange(rowIndex, fieldKey, e.target.value, Object.keys(fieldData)[0])}
                            variant="outlined"
                        >
                            {selectOptions.map((option: any) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'autocomplete':
                const autocompleteOptions = (() => {
                    if (fieldDef.anyOf && Array.isArray(fieldDef.anyOf)) {
                        for (const option of fieldDef.anyOf) {
                            if (option.enum) return option.enum;
                        }
                    }
                    return fieldDef.enum || [];
                })();

                return (
                    <Autocomplete
                        freeSolo
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        options={autocompleteOptions.filter((opt: string) => opt !== '')}
                        value={value || null}
                        onInputChange={(_, newInputValue, reason) => {
                            if (reason === 'input' && newInputValue !== '') {
                                handleCellChange(
                                    rowIndex,
                                    fieldKey,
                                    newInputValue,
                                    Object.keys(fieldData)[0]
                                );
                            }
                        }}
                        onChange={(_, newValue) => {
                            handleCellChange(
                                rowIndex,
                                fieldKey,
                                newValue || '',
                                Object.keys(fieldData)[0]
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                variant="outlined"
                                required={required}
                                fullWidth
                            />
                        )}
                        size="small"
                    />
                );
            default: // text
                return (
                    <TextField
                        value={value}
                        required={required}
                        onChange={(e) => handleCellChange(rowIndex, fieldKey, e.target.value, Object.keys(fieldData)[0])}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                );
        }
    };

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormLabel required={required} color="secondary" className="form-separate-label">
                {label}
            </FormLabel>
            <Box sx={{
                width: `${containerWidth}px`,
                maxWidth: '100%',
                overflow: 'hidden',
                marginTop: '10px',
            }}>
                <TableContainer component={Paper} sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    border: hasError ? '1px solid #d32f2f' : 'none', // Red border when hasError is true
                    borderRadius: hasError ? '4px' : '0' // Optional: add border radius for better appearance
                }}>
                    <Table sx={{ minWidth: CELL_WIDTH * (headerLabels.length + 1) }}>
                        <TableHead>
                            <TableRow>
                                {headerLabels.map((label, index) => (
                                    <TableCell
                                        key={index}
                                        sx={{
                                            width: CELL_WIDTH,
                                            minWidth: CELL_WIDTH,
                                            maxWidth: CELL_WIDTH,
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            padding: '8px'
                                        }}
                                    >
                                        {label}
                                    </TableCell>
                                ))}
                                <TableCell
                                    sx={{
                                        width: SMALL_CELL_WIDTH,
                                        minWidth: SMALL_CELL_WIDTH,
                                        maxWidth: SMALL_CELL_WIDTH,
                                        fontWeight: 'bold',
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px'
                                    }}
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.map((row: any, rowIndex: number) => (
                                <TableRow key={rowIndex}>
                                    {Object.entries(row).map(([fieldKey, fieldData], cellIndex) => (
                                        <TableCell
                                            key={`${rowIndex}-${cellIndex}`}
                                            sx={{
                                                width: CELL_WIDTH,
                                                minWidth: CELL_WIDTH,
                                                maxWidth: CELL_WIDTH,
                                                padding: '8px'
                                            }}
                                        >
                                            {renderCell(fieldKey, fieldData, rowIndex)}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        sx={{
                                            width: SMALL_CELL_WIDTH,
                                            minWidth: SMALL_CELL_WIDTH,
                                            maxWidth: SMALL_CELL_WIDTH,
                                            padding: '8px'
                                        }}
                                    >
                                        {rowIndex > 0 && allowAddRows && (
                                            <IconButton onClick={() => removeRow(rowIndex)} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {showDescription && (
                    <FormHelperText sx={{ mt: 1 }}>
                        {description}
                    </FormHelperText>
                )}

                {allowAddRows && (
                    <Button
                        onClick={addRow}
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        sx={{
                            mt: 1,
                            mb: 2
                        }}
                    >
                        Add Row
                    </Button>
                )}

                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default Columns;
